/**
 * Secure model transfer to the Buffer TEE — three-step protocol:
 *
 * 1. SHA-256 hash each file in 16 MB streaming chunks (peak: ~16 MB)
 * 2. POST /v1/submit  (HPKE-encrypted, tiny)
 *    { dataset_id, model_format, model_sha256, adaptor_sha256,
 *      weights_sha256?, input_spec_sha256? }
 *    → Buffer TEE creates job, returns { job_id }
 * 3. PUT /v1/upload/:job_id/:slot   (AES-256-GCM-CHUNKED-v1, 16 MB chunks)
 *    slot = model | weights (ONNX external data, optional) | adaptor | input_spec (optional)
 *    → Buffer TEE: HPKE-unwraps AES key, decrypts chunks, verifies SHA-256,
 *      queues the job once every committed file has arrived
 *
 * The Processing TEE then runs the model, the owner's adaptor.py and the
 * dataset's evaluator, and posts the score to the leaderboard; see
 * fetchJobStatus / fetchJobResults.
 *
 * SHA-256 hashes are committed via HPKE before files arrive, so a MITM cannot
 * substitute files even if they intercept the upload channel.
 * AES-256-GCM per-chunk authentication tags prevent any in-transit modification.
 * HPKE key-wrap ensures only the attested TEE can decrypt the AES key.
 */

import { sha256 as nobleSha256 } from "@noble/hashes/sha2.js"
import { attestAndOpen } from "./index"
import { LEADERBOARD_API_BASE } from "./constants"
import type { OpenedSession } from "./types"

const HASH_CHUNK = 16 * 1024 * 1024  // 16 MB — streaming hash chunk

/** Model file formats the Processing TEE runs. */
export type ModelFormat = "onnx" | "torchscript" | "huggingface"
const MODEL_FORMATS: readonly ModelFormat[] = ["onnx", "torchscript", "huggingface"]

/** The dataset a model is scored on: its catalogue UUID. */
export type DatasetRef = string

export type StepName =
  | "attest_start"
  | "attest_done"
  | "model_hash"
  | "submit_meta"
  | "upload_model"
  | "upload_weights"
  | "upload_python"      // the adaptor.py upload (step name kept for existing UIs)
  | "upload_input_spec"
  | "done"
  | "error"

// Steps that belong to the upload phase only (post-attestation).
export type UploadStepName = Exclude<StepName, "attest_start" | "attest_done">

export interface JobEvaluationAttestation {
  hwmodel:      string
  swname:       string
  image_digest: string
  secboot:      boolean
  iss:          string
}

/** A finished job's leaderboard entry (the Processing TEE's submission). */
export interface JobEvaluationResult {
  job_id:                 string    // Buffer TEE job id
  leaderboard_job_id:     string    // uuid5 of the job id, as stored by the leaderboard
  dataset_id:             string    // catalogue UUID
  status:                 "succeeded"
  /** metrics["accuracy"] when the dataset's leaderboard metrics include it, else NaN. */
  accuracy:               number
  /** The metrics the dataset's catalogue entry requires, by catalogue key (e.g. f2_score). */
  metrics:                Record<string, unknown>
  num_samples:            number
  elapsed_seconds:        number
  model_sha256:           string
  onnx_runtime_providers: string[]
  attestation:            JobEvaluationAttestation
  is_selected:            boolean
}

/** Buffer TEE job states, in order; complete and error are final. */
export type JobState = "pending_upload" | "queued" | "dispatched" | "complete" | "error"

export interface JobStatusInfo {
  jobId:        string
  state:        JobState | string
  datasetId?:   string
  modelFormat?: string
  /** Set when state is "error": the Processing TEE's classified failure. */
  error?:       { error_code?: number; error_type?: string; error_message?: string }
}

export interface TransferOptions {
  teeUrl:         string
  /** Defaults to "onnx". */
  modelFormat?:   ModelFormat | undefined
  modelFile:      File
  /** ONNX external weights (.onnx.data); optional, and ONNX only. */
  weightsFile?:   File | undefined
  /** adaptor.py: turns the model's raw outputs into predictions.csv. Required. */
  adaptorFile?:   File | undefined
  /** @deprecated The old preprocessing-script slot; it is uploaded as the adaptor. Use adaptorFile. */
  pythonFile?:    File | undefined
  /** input_spec.json (ONNX / TorchScript): input size and resize method, e.g. {"input_size": [224, 224]}. */
  inputSpec?:     File | Record<string, unknown> | undefined
  datasetId:      DatasetRef
  keycloakToken?: string    // forwarded to attestAndOpen and all subsequent TEE requests
  onStep?:        (step: StepName, detail: string) => void
}

export interface TransferResult {
  oidc:      OpenedSession["oidc"]
  response:  unknown
  elapsedMs: number
}

// SubmitOptions is used when the caller has already attested and holds an
// OpenedSession — e.g. to show the attestation report before uploading.
export type TransferPhase =
  | "model_hash"
  | "submit_meta"
  | "upload_model"
  | "upload_weights"
  | "upload_python"      // adaptor.py
  | "upload_input_spec"

export interface TransferCheckpoint {
  modelSHA256?:     string
  weightsSHA256?:   string
  /** SHA-256 of adaptor.py (field name kept for existing callers). */
  pythonSHA256?:    string
  inputSpecSHA256?: string
  jobId?:           string
}

export interface SubmitOptions {
  teeUrl:       string
  session:      OpenedSession
  /** Defaults to "onnx". */
  modelFormat?: ModelFormat | undefined
  modelFile:    File
  /** ONNX external weights (.onnx.data); optional, and ONNX only. */
  weightsFile?: File | undefined
  /** adaptor.py: turns the model's raw outputs into predictions.csv. Required. */
  adaptorFile?: File | undefined
  /** @deprecated The old preprocessing-script slot; it is uploaded as the adaptor. Use adaptorFile. */
  pythonFile?:  File | undefined
  /** input_spec.json (ONNX / TorchScript): input size and resize method, e.g. {"input_size": [224, 224]}. */
  inputSpec?:   File | Record<string, unknown> | undefined
  datasetId:    DatasetRef
  onStep?:      (step: UploadStepName, detail: string) => void
  /** Resume after a failed phase; earlier completed phases are skipped. */
  resumeFrom?:  TransferPhase
  /** Hashes / job id from a previous attempt; mutated as phases complete. */
  checkpoint?:  TransferCheckpoint
  /**
   * Re-attest and return a fresh HPKE session. Called after local hashing,
   * which can outlive the session opened before the upload started.
   */
  renewSession?: () => Promise<OpenedSession>
}

export interface SubmitResult {
  jobId:            string
  elapsedMs:        number
  /** The catalogue UUID the job was submitted for. */
  datasetId:        string
  modelFormat:      ModelFormat
  modelSHA256:      string
  weightsSHA256?:   string
  adaptorSHA256:    string
  /** Same as adaptorSHA256 (kept for existing callers). */
  pythonSHA256?:    string
  inputSpecSHA256?: string
}

const TRANSFER_PHASE_ORDER: TransferPhase[] = [
  "model_hash",
  "submit_meta",
  "upload_model",
  "upload_weights",
  "upload_python",
  "upload_input_spec",
]

/** The catalogue UUID for a dataset reference. */
export function resolveDatasetId(ref: DatasetRef): string {
  const id = ref.trim()
  if (!id) throw new Error("dataset_id is empty; submit the dataset's catalogue UUID.")
  return id
}

/** input_spec.json as a File (an object is serialised; the same object always hashes the same). */
function inputSpecAsFile(spec: File | Record<string, unknown> | undefined): File | undefined {
  if (spec === undefined) return undefined
  if (spec instanceof File) return spec
  return new File([JSON.stringify(spec)], "input_spec.json", { type: "application/json" })
}

function shouldRunPhase(resumeFrom: TransferPhase | undefined, phase: TransferPhase): boolean {
  if (!resumeFrom) {
    return true
  }
  return TRANSFER_PHASE_ORDER.indexOf(phase) >= TRANSFER_PHASE_ORDER.indexOf(resumeFrom)
}

/**
 * GET /v1/status/{job_id} on the Buffer TEE — the job's state, or null if the
 * Buffer does not know the job (or is unreachable). Only the fields the UI
 * needs are returned: the Buffer's job record also carries the submitter's token.
 */
export async function fetchJobStatus(
  session: OpenedSession,
  jobId: string,
): Promise<JobStatusInfo | null> {
  const payload = await session.get(`/v1/status/${encodeURIComponent(jobId)}`)
  if (!payload || typeof payload !== "object") {
    return null
  }
  const job = (payload as Record<string, unknown>)["job"]
  if (!job || typeof job !== "object") {
    return null
  }
  const j = job as Record<string, unknown>
  const info: JobStatusInfo = { jobId, state: typeof j["status"] === "string" ? j["status"] : "unknown" }
  if (typeof j["dataset_id"] === "string") info.datasetId = j["dataset_id"]
  if (typeof j["model_format"] === "string") info.modelFormat = j["model_format"]
  const err = j["completion_error"]
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>
    info.error = {
      ...(typeof e["error_code"] === "number" ? { error_code: e["error_code"] } : {}),
      ...(typeof e["error_type"] === "string" ? { error_type: e["error_type"] } : {}),
      ...(typeof e["error_message"] === "string" ? { error_message: e["error_message"] } : {}),
    }
  }
  return info
}

// RFC 4122 NAMESPACE_URL, as used by the Processing TEE (internal/leaderboard/uuid5.go).
const UUID_NAMESPACE_URL = "6ba7b8119dad11d180b400c04fd430c8"

/**
 * The id the Processing TEE posts a job under on the leaderboard:
 * uuid5(NAMESPACE_URL, "tanuh:" + Buffer job id).
 */
export async function leaderboardJobId(bufferJobId: string): Promise<string> {
  const ns = Uint8Array.from(UUID_NAMESPACE_URL.match(/../g)!.map(h => parseInt(h, 16)))
  const name = new TextEncoder().encode(`tanuh:${bufferJobId}`)
  const data = new Uint8Array(ns.length + name.length)
  data.set(ns, 0)
  data.set(name, ns.length)
  const u = new Uint8Array(await crypto.subtle.digest("SHA-1", data)).slice(0, 16)
  u[6] = (u[6]! & 0x0f) | 0x50  // version 5
  u[8] = (u[8]! & 0x3f) | 0x80  // RFC 4122 variant
  const hex = Array.from(u).map(b => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * The signed-in user's leaderboard entry for a job, or null if it is not
 * there (yet). GET {LEADERBOARD_API_BASE}/my-submissions?dataset_id=<uuid>.
 */
export async function fetchLeaderboardSubmission(
  session: OpenedSession,
  datasetId: string,
  bufferJobId: string,
): Promise<Record<string, unknown> | null> {
  const token = await session.bearerToken()
  const url = `${LEADERBOARD_API_BASE}/my-submissions?dataset_id=${encodeURIComponent(datasetId)}`
  const r = await fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
  if (!r.ok) {
    throw new Error(`leaderboard my-submissions failed: ${r.status}`)
  }
  const body = await r.json() as { submissions?: unknown }
  const want = await leaderboardJobId(bufferJobId)
  const list = Array.isArray(body.submissions) ? body.submissions : []
  const hit = list.find(s => s && typeof s === "object" && (s as Record<string, unknown>)["job_id"] === want)
  return (hit as Record<string, unknown> | undefined) ?? null
}

/**
 * Polling helper: null while the job is still waiting, uploading or running;
 * the leaderboard entry once it has been scored; throws when it failed.
 */
export async function fetchJobResults(
  session: OpenedSession,
  jobId: string,
): Promise<JobEvaluationResult | null> {
  const status = await fetchJobStatus(session, jobId)
  if (!status) {
    return null
  }
  if (status.state === "error") {
    const e = status.error
    throw new Error(`Evaluation failed${e?.error_type ? ` (${e.error_type})` : ""}: ` +
      (e?.error_message ?? "see the job status for details"))
  }
  if (status.state !== "complete" || !status.datasetId) {
    return null
  }
  const entry = await fetchLeaderboardSubmission(session, status.datasetId, jobId)
  if (!entry) {
    return null  // the Buffer marks the job complete right after the leaderboard write
  }
  if (entry["status"] !== "succeeded") {
    throw new Error(`Evaluation failed${entry["error_type"] ? ` (${String(entry["error_type"])})` : ""}: ` +
      String(entry["error_message"] ?? "no details"))
  }
  const metrics = (entry["metrics"] && typeof entry["metrics"] === "object"
    ? entry["metrics"] : {}) as Record<string, unknown>
  return {
    job_id:                 jobId,
    leaderboard_job_id:     String(entry["job_id"]),
    dataset_id:             status.datasetId,
    status:                 "succeeded",
    accuracy:               typeof metrics["accuracy"] === "number" ? metrics["accuracy"] : Number.NaN,
    metrics,
    num_samples:            Number(entry["num_samples"] ?? 0),
    elapsed_seconds:        Number(entry["elapsed_seconds"] ?? 0),
    model_sha256:           String(entry["model_sha256"] ?? ""),
    onnx_runtime_providers: Array.isArray(entry["onnx_runtime_providers"])
      ? (entry["onnx_runtime_providers"] as string[]) : [],
    attestation:            entry["attestation"] as JobEvaluationAttestation,
    is_selected:            entry["is_selected"] === true,
  }
}

// submitOnnxModel handles the post-attestation upload steps using an
// already-opened session returned by attestAndOpen().
export async function submitOnnxModel(opts: SubmitOptions): Promise<SubmitResult> {
  let session = opts.session
  const {
    modelFile,
    weightsFile,
    datasetId,
    onStep = () => {},
    resumeFrom,
    checkpoint = {},
  } = opts
  const modelFormat: ModelFormat = opts.modelFormat ?? "onnx"
  const adaptorFile = opts.adaptorFile ?? opts.pythonFile
  const inputSpecFile = inputSpecAsFile(opts.inputSpec)

  // ── 0. Check the submission before hashing gigabytes ─────────────────────
  if (!MODEL_FORMATS.includes(modelFormat)) {
    throw new Error(`Unsupported model format "${modelFormat}"; use onnx, torchscript or huggingface.`)
  }
  if (!adaptorFile) {
    throw new Error(
      "adaptor.py is required: it turns the model's raw outputs into predictions.csv " +
      "(templates: adaptor_sigmoid_binary.py for binary, adaptor_softmax_multiclass.py for multi-class datasets).")
  }
  if (weightsFile && modelFormat !== "onnx") {
    throw new Error("A weights file (.onnx.data) is only used with ONNX models.")
  }
  if (inputSpecFile && modelFormat === "huggingface") {
    throw new Error("An input spec is only used with ONNX and TorchScript; Hugging Face models use their preprocessor_config.json.")
  }
  const datasetUUID = resolveDatasetId(datasetId)

  const t0 = performance.now()
  let from = resumeFrom
  if (
    (from === "upload_model" || from === "upload_weights" || from === "upload_python" ||
      from === "upload_input_spec") &&
    !checkpoint.jobId
  ) {
    from = checkpoint.modelSHA256 ? "submit_meta" : "model_hash"
  }
  const run = (phase: TransferPhase) => shouldRunPhase(from, phase)

  // ── 1. Streaming SHA-256 (16 MB at a time, ~16 MB peak) ──────────────────
  const needsHashes =
    run("model_hash") ||
    !checkpoint.modelSHA256 ||
    (weightsFile !== undefined && !checkpoint.weightsSHA256) ||
    !checkpoint.pythonSHA256 ||
    (inputSpecFile !== undefined && !checkpoint.inputSpecSHA256)

  if (needsHashes) {
    if (run("model_hash")) {
      onStep("model_hash", `Hashing model (${fmt(modelFile.size)})…`)
    }
    checkpoint.modelSHA256 = await computeSHA256Streaming(modelFile)

    if (weightsFile) {
      if (run("model_hash")) {
        onStep("model_hash", `Hashing weights (${fmt(weightsFile.size)})…`)
      }
      checkpoint.weightsSHA256 = await computeSHA256Streaming(weightsFile)
    } else {
      delete checkpoint.weightsSHA256
    }

    if (run("model_hash")) {
      onStep("model_hash", `Hashing adaptor.py (${fmt(adaptorFile.size)})…`)
    }
    checkpoint.pythonSHA256 = await computeSHA256Streaming(adaptorFile)

    if (inputSpecFile) {
      checkpoint.inputSpecSHA256 = await computeSHA256Streaming(inputSpecFile)
    } else {
      delete checkpoint.inputSpecSHA256
    }

    if (run("model_hash")) {
      onStep("model_hash", "SHA-256 computed")
    }
  }

  const modelSHA256 = checkpoint.modelSHA256
  const weightsSHA256 = weightsFile ? checkpoint.weightsSHA256 : undefined
  const adaptorSHA256 = checkpoint.pythonSHA256
  const inputSpecSHA256 = inputSpecFile ? checkpoint.inputSpecSHA256 : undefined
  if (!modelSHA256 || !adaptorSHA256 || (weightsFile && !weightsSHA256) ||
    (inputSpecFile && !inputSpecSHA256)) {
    throw new Error("SHA-256 hashes are missing; cannot continue transfer.")
  }

  // Hashing can outlive the session opened before the upload started.
  if (opts.renewSession) {
    try {
      session = await opts.renewSession()
    } catch (err) {
      onStep("error", `Session refresh failed: ${err instanceof Error ? err.message : err}`)
      throw err
    }
  }

  // ── 2. HPKE-encrypt tiny metadata → POST /v1/submit ──────────────────────
  const needsSubmit = run("submit_meta") || !checkpoint.jobId
  if (needsSubmit) {
    onStep("submit_meta", "Sending HPKE-encrypted job metadata…")
    let response: unknown
    try {
      response = await session.send("/v1/submit", {
        dataset_id:     datasetUUID,
        model_format:   modelFormat,
        model_sha256:   modelSHA256,
        adaptor_sha256: adaptorSHA256,
        ...(weightsSHA256 !== undefined ? { weights_sha256: weightsSHA256 } : {}),
        ...(inputSpecSHA256 !== undefined ? { input_spec_sha256: inputSpecSHA256 } : {}),
      })
    } catch (err) {
      onStep("error", `Metadata submit failed: ${err instanceof Error ? err.message : err}`)
      throw err
    }

    const jobResult = response as Record<string, unknown>
    const submittedJobId = jobResult['job_id'] as string
    if (!submittedJobId) throw new Error(`No job_id in response: ${JSON.stringify(response)}`)
    checkpoint.jobId = submittedJobId
  }

  const jobId = checkpoint.jobId
  if (!jobId) {
    throw new Error("No job_id available; cannot continue upload.")
  }

  // ── 3. Envelope-encrypted chunked upload, one slot per file ──────────────
  // The Buffer TEE queues the job once every committed file has arrived.
  const upload = async (phase: UploadStepName & TransferPhase, slot: string, label: string,
    file: File, sha256hex: string) => {
    onStep(phase, `Encrypting + uploading ${label} (${fmt(file.size)})…`)
    try {
      await session.uploadEncrypted(`/v1/upload/${jobId}/${slot}`, file, sha256hex,
        pct => onStep(phase, `${pct}%`))
    } catch (err) {
      onStep("error", `${label} upload failed: ${err instanceof Error ? err.message : err}`)
      throw err
    }
  }
  if (run("upload_model")) {
    await upload("upload_model", "model", "model", modelFile, modelSHA256)
  }
  if (weightsFile && weightsSHA256 && run("upload_weights")) {
    await upload("upload_weights", "weights", "weights", weightsFile, weightsSHA256)
  }
  if (run("upload_python")) {
    await upload("upload_python", "adaptor", "adaptor.py", adaptorFile, adaptorSHA256)
  }
  if (inputSpecFile && inputSpecSHA256 && run("upload_input_spec")) {
    await upload("upload_input_spec", "input_spec", "input_spec.json", inputSpecFile, inputSpecSHA256)
  }

  const elapsedMs = Math.round(performance.now() - t0)
  onStep("done", `Job queued in ${fmtDuration(elapsedMs)}`)
  return {
    jobId,
    elapsedMs,
    datasetId: datasetUUID,
    modelFormat,
    modelSHA256,
    adaptorSHA256,
    pythonSHA256: adaptorSHA256,
    ...(weightsSHA256 !== undefined ? { weightsSHA256 } : {}),
    ...(inputSpecSHA256 !== undefined ? { inputSpecSHA256 } : {}),
  }
}

// transferOnnxModel is the original one-shot convenience function.
// It attests the Buffer TEE and immediately uploads the model and its files.
// For a two-step flow (show attestation report before uploading),
// call attestAndOpen() then submitOnnxModel() separately.
export async function transferOnnxModel(opts: TransferOptions): Promise<TransferResult> {
  const { teeUrl, keycloakToken, onStep = () => {} } = opts
  const t0 = performance.now()

  // ── 1. RA-TLS attestation ─────────────────────────────────────────────────
  onStep("attest_start", `Attesting Buffer TEE at ${teeUrl}…`)
  let session: OpenedSession
  try {
    session = await attestAndOpen({
      attestUrl: teeUrl,
      ...(keycloakToken !== undefined ? { keycloakToken } : {}),
    })
  } catch (err) {
    onStep("error", `Attestation failed: ${err instanceof Error ? err.message : err}`)
    throw err
  }
  onStep("attest_done",
    `Digest: ${session.oidc.submods?.container?.image_digest ?? "—"} · HW: ${session.oidc.hwmodel ?? "—"}`)

  // ── 2–4. Hash + submit + upload (delegated) ───────────────────────────────
  const { jobId } = await submitOnnxModel({
    teeUrl,
    session,
    modelFormat: opts.modelFormat,
    modelFile:   opts.modelFile,
    weightsFile: opts.weightsFile,
    adaptorFile: opts.adaptorFile,
    pythonFile:  opts.pythonFile,
    inputSpec:   opts.inputSpec,
    datasetId:   opts.datasetId,
    onStep: onStep as (step: UploadStepName, detail: string) => void,
  })

  const elapsedMs = Math.round(performance.now() - t0)
  return { oidc: session.oidc, response: { job_id: jobId, status: "queued" }, elapsedMs }
}

async function computeSHA256Streaming(file: File): Promise<string> {
  const hasher = nobleSha256.create()
  let offset = 0
  while (offset < file.size) {
    const buf = await file.slice(offset, offset + HASH_CHUNK).arrayBuffer()
    hasher.update(new Uint8Array(buf))
    offset += HASH_CHUNK
  }
  const digest = hasher.digest()
  return Array.from(digest).map(b => b.toString(16).padStart(2, "0")).join("")
}

function fmt(n: number): string {
  if (n < 1024)        return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

function fmtDuration(ms: number): string {
  const seconds = ms / 1000
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return `${minutes}m ${remainder.toFixed(1)}s`
}
